import { Injectable, inject, signal } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from 'supabase/types/supabase';
import { AuthService } from '../auth/auth.service';
import { LoggingService } from '../logging/logging.service';
import { LocalStorageService } from '../storage/local-storage.service';

export interface InviteCode {
  invite_code: string;
  fromUrl: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class InviteService {
  private readonly authService = inject(AuthService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly logger = inject(LoggingService);
  private readonly supabaseClient =
    inject<SupabaseClient<Database>>(SupabaseClient);

  private readonly INVITE_CODE_KEY = 'pending_invite_code';
  private readonly CODE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  // Signal for pending invite code
  readonly pendingInviteCode = signal<InviteCode | null>(null);
  constructor() {
    // Load any pending code from storage on initialization
    this.loadPendingCode();
  }

  /**
   * Store an invite code for later redemption
   * @param invite_code The invitation code
   * @param fromUrl The original URL the code came from
   */
  storeInviteCode(invite_code: string, fromUrl: string) {
    const inviteCode: InviteCode = {
      invite_code,
      fromUrl,
      timestamp: Date.now(),
    };

    // Store in local storage for persistence
    this.localStorage.setItem(this.INVITE_CODE_KEY, JSON.stringify(inviteCode));

    // Update signal for reactive UI
    this.pendingInviteCode.set(inviteCode);

    this.logger.log('Stored invite code for redemption');
  }

  /**
   * Load pending code from storage
   */
  private loadPendingCode() {
    try {
      const stored = this.localStorage.getItem(
        this.INVITE_CODE_KEY,
      ) as InviteCode;
      const inviteCode = stored?.invite_code;

      if (!inviteCode) {
        return;
      }

      // Check if code is not expired (7 days)
      if (Date.now() - stored.timestamp < this.CODE_EXPIRY_MS) {
        this.pendingInviteCode.set(stored);
      } else {
        // Code expired, clear it
        this.clearStoredCode();
      }
    } catch (error) {
      this.logger.error('Error loading pending code:', error);
    }
  }

  /**
   * Redeem a stored invite code
   */
  async redeemStoredCode(): Promise<void> {
    const inviteCode = this.pendingInviteCode();

    if (!inviteCode) {
      return;
    }

    // Get authenticated user
    const authUser = await this.authService.getAuthUser();

    if (!authUser) {
      throw new Error('User must be authenticated to redeem invitation');
    }

    // Call the database function to redeem the invitation
    const { data, error } = await this.supabaseClient.rpc('redeem_invitation', {
      p_invite_code: inviteCode.invite_code,
      p_viewer_id: authUser.id,
    });

    if (error) {
      throw error;
    }

    // Clear the stored code
    this.clearStoredCode();
  }

  /**
   * Clear stored invite code
   */
  clearStoredCode() {
    this.localStorage.removeItem(this.INVITE_CODE_KEY);
    this.pendingInviteCode.set(null);
  }

  /**
   * Check if there's a pending invite code
   */
  hasPendingCode(): boolean {
    return this.pendingInviteCode() !== null;
  }
}
